import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule, TitleStrategy } from '@angular/router';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { CommonModule, DatePipe } from '@angular/common';
import { AuthHttpInterceptor, AuthModule } from '@auth0/auth0-angular';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { routerReducer, StoreRouterConnectingModule } from '@ngrx/router-store';
import {
  MATERIAL_FORM_FIELD_DEFAULTS,
  MATERIAL_RADIO_DEFAULTS,
  MATERIAL_TAB_DEFAULTS,
} from '@cp/shared/styles';
import { EnvironmentModule } from '@cp/shared/environment/environment.module';
import { Environment } from '@cp/shared/environment/environment.interface';
import { register } from 'swiper/element/bundle';
import { ServiceWorkerModule } from '@angular/service-worker';
import { CookieService } from 'ngx-cookie-service';
import { SharedUiModule } from '@cp/ui/shared-ui.module';
import { CompliancePortalSharedModule } from '@cp/shared/core/compliance-portal-shared.module';
import { SharedDataAccessModule } from '@cp/shared/data-access/shared-data-access.module';
import { ErrorInterceptor } from '@cp/shared/data-access/shared/interceptors/error.interceptor';
import { USEFUL_LINKS_CONFIG } from '@cp/shared/core/tokens/useful-links.token';
import { environment } from '../environments/environment';
import { AppComponent } from './app.component';
import { HeaderComponent } from './components/header/header.component';
import { MainComponent } from './components/main/main.component';
import { NavigationComponent } from './components/navigation/navigation.component';
import { AppRoutingModule } from './app-routing.module';
import { PageNotFoundComponent } from './components/page-not-found/page-not-found.component';
import { BasicInterceptor } from './interceptors/basic.interceptor';
import { BottomNavigationComponent } from './components/bottom-navigation/bottom-navigation.component';
import { TemplatePageTitleStrategy } from './services/template-page-title.service';
import { LogInTargetComponent } from './components/log-in-target/log-in-target.component';
import { LogOutComponent } from './components/log-out/log-out.component';
import { CallbackComponent } from './components/callback/callback.component';
import { PwaBannerComponent } from './components/pwa-banner/pwa-banner.component';
import { ErrorComponent } from './components/error/error.component';
import { UsefulLinksService } from './services/useful-links.service';
import { HeaderInterceptor } from './interceptors/header.interceptor';

register();

@NgModule({
  declarations: [
    AppComponent,
    HeaderComponent,
    MainComponent,
    NavigationComponent,
    BottomNavigationComponent,
    PageNotFoundComponent,
    LogInTargetComponent,
    LogOutComponent,
    CallbackComponent,
    PwaBannerComponent,
    ErrorComponent,
  ],
  imports: [
    EnvironmentModule.withEnvironment<Environment>(environment),
    AuthModule.forRoot({
      clientId: environment.auth0.clientId,
      domain: environment.auth0.domain,
      cacheLocation: environment.production ? 'memory' : 'localstorage',
      authorizationParams: {
        audience: environment.apiEndpoint,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        redirect_uri: environment.auth0.redirectUrl,
        // todo: remove env values, error
        // 'ext-swap_org_uri': environment.auth0.swapOrgUrl,
        // organization: environment.auth0.organization,
      },
      errorPath: environment.auth0.errorPath,
      useRefreshTokens: true,
      // https://community.auth0.com/t/auth0-spa-2-x-returning-missing-refresh-token/98999/18
      // As we are using custom-domains for everything now, this is OK to turn on.
      useRefreshTokensFallback: true,
      httpInterceptor: {
        allowedList: [`${environment.apiEndpoint}/*`],
      },
    }),
    CommonModule,
    BrowserModule,
    BrowserAnimationsModule,
    RouterModule,
    AppRoutingModule,
    HttpClientModule,
    SharedDataAccessModule,
    SharedUiModule,
    EffectsModule.forRoot([]),
    StoreRouterConnectingModule.forRoot(),
    StoreModule.forRoot(
      { router: routerReducer },
      {
        runtimeChecks: {
          strictActionImmutability: true,
          strictStateImmutability: true,
        },
      },
    ),
    StoreDevtoolsModule.instrument({
      maxAge: 25,
      autoPause: true,
      logOnly: environment.production,
    }),
    CompliancePortalSharedModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.pwa,
      registrationStrategy: 'registerWhenStable:30000',
    }),
    SharedUiModule,
  ],
  providers: [
    { provide: TitleStrategy, useClass: TemplatePageTitleStrategy },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: BasicInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthHttpInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HeaderInterceptor,
      multi: true,
    },
    MATERIAL_FORM_FIELD_DEFAULTS,
    MATERIAL_RADIO_DEFAULTS,
    MATERIAL_TAB_DEFAULTS,
    DatePipe,
    CookieService,
    {
      provide: USEFUL_LINKS_CONFIG,
      useFactory: (service: UsefulLinksService) =>
        service.getUsefulLinksConfig(),
      deps: [UsefulLinksService],
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
